import { needsSubmissions, requestTypeMeta } from "./requestTypes";

/**
 * The request wizard's steps, in order — the one place that decides them.
 *
 *   Request   → which of the three requests
 *   Patient   → "Find or Create Patient", or "Upload as File"
 *   Policies  → pick each policy and approve it where it opens
 *   Estimates / Claims → what to chase (estimates and claim follow-ups only)
 *
 * A launch that already knows something skips the step that asks it: the
 * insurance modal, a treatment card, the estimates modal and the claim
 * follow-ups pane all seed the patient, and most seed the type.
 *
 * The submissions step is skipped when the patient came from an uploaded claim
 * form: that form IS the submission — what was read off it, and the file itself,
 * go with the request — so asking for it again would be asking twice.
 *
 * @param {Object}  opts
 * @param {boolean} opts.seededType     the type was given at launch
 * @param {boolean} opts.seededPatient  the patient was given at launch
 * @param {string}  opts.requestType    "breakdown" | "estimate" | "claim_followup" | null
 * @param {string}  opts.patientSource  "find" | "upload" | null
 * @returns {Array<{key: string, title: string}>}
 */
export function composerSteps({ seededType, seededPatient, requestType, patientSource } = {}) {
  const steps = [];
  if (!seededType) steps.push({ key: "type", title: "Request" });
  if (!seededPatient) steps.push({ key: "patient", title: "Patient" });
  steps.push({ key: "policies", title: "Policies" });
  if (requestType && needsSubmissions(requestType) && patientSource !== "upload") {
    steps.push({ key: "submissions", title: requestTypeMeta(requestType).submissionsStepTitle });
  }
  return steps;
}

/** The step after `key`, or null on the last one. */
export function nextStepKey(steps, key) {
  const i = steps.findIndex(function (s) { return s.key === key; });
  return i === -1 || i === steps.length - 1 ? null : steps[i + 1].key;
}

/** The step before `key`, or null on the first one. */
export function previousStepKey(steps, key) {
  const i = steps.findIndex(function (s) { return s.key === key; });
  return i <= 0 ? null : steps[i - 1].key;
}

export default composerSteps;
